import { lazy, Suspense, useState } from "react";
import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Home, Search, PlusCircle, Mail, User as UserIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { getUnreadCount } from "@/lib/messages.functions";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { planAtLeast } from "@/lib/plans";
import { cn } from "@/lib/utils";
import { useLocale, useTranslation } from "@/lib/i18n";
import { localizedPath } from "@/lib/i18n/slug-map";

const AuthModal = lazy(() =>
  import("@/components/auth/AuthModal").then((module) => ({ default: module.AuthModal })),
);
const MobileCreateSheet = lazy(() =>
  import("@/components/MobileCreateSheet").then((module) => ({
    default: module.MobileCreateSheet,
  })),
);

export function MobileTabBar() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLocale();
  const local = (to: string) => localizedPath(to, lang);
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const pathname = useRouterState({ select: (r) => r.location.pathname });

  const fetchUnread = useServerFn(getUnreadCount);
  const { data: unread } = useQuery({
    queryKey: ["unread", user?.id],
    queryFn: () => fetchUnread({}),
    enabled: !!user,
    refetchInterval: 15000,
  });
  const unreadCount = unread?.count ?? 0;

  const { data: planStatus } = useQuery({
    queryKey: ["plan-status"],
    queryFn: () => getMyPlanStatus(),
    enabled: !!user,
  });

  const itemClass = (active: boolean) =>
    cn(
      "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-0.5 pb-1 pt-1.5 text-center text-[11px] font-medium leading-none transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
      active ? "text-primary" : "text-muted-foreground",
    );

  const requireAuth = (to: string) => {
    if (user) navigate({ to });
    else setAuthOpen(true);
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background pb-[var(--safe-bottom)] shadow-[0_-1px_10px_rgba(12,35,64,0.06)] supports-[backdrop-filter]:bg-background/95 supports-[backdrop-filter]:backdrop-blur md:hidden"
        aria-label={t("common.quickMenu")}
      >
        <div className="flex h-[var(--tabbar-h)] items-stretch [&>*]:min-h-11">
           <Link to={local("/")} className={itemClass(pathname === local("/"))}>
            <Home className="h-5 w-5" />
            {t("common.home")}
          </Link>
           <Link to={local("/annonser")} className={itemClass(pathname.startsWith(local("/annonser")))}>
            <Search className="h-5 w-5" />
            {t("common.listings")}
          </Link>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className={itemClass(pathname.startsWith("/skapa"))}
          >
            <PlusCircle className="h-5 w-5 text-primary" />
            {t("common.create")}
          </button>

          <button
            type="button"
            onClick={() => requireAuth("/meddelanden")}
            className={cn(itemClass(pathname.startsWith("/meddelanden")), "relative")}
          >
            <span className="relative">
              <Mail className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -right-2 -top-1.5 min-w-4 rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground">
                  {unreadCount}
                </span>
              )}
            </span>
            {t("common.inbox")}
          </button>
          <button
            type="button"
            onClick={() => requireAuth("/oversikt")}
            className={itemClass(
              pathname.startsWith("/oversikt") ||
                pathname.startsWith("/konto") ||
                pathname.startsWith("/mina-annonser") ||
                pathname.startsWith("/adresser") ||
                pathname.startsWith("/mina-prylar") ||
                pathname.startsWith("/videoskanning") ||
                pathname.startsWith("/statistik") ||
                pathname.startsWith("/profil"),
            )}
          >
            <UserIcon className="h-5 w-5" />
            {user ? t("common.myPage") : t("common.signIn")}
          </button>
        </div>
      </nav>

      {createOpen && (
        <Suspense fallback={null}>
          <MobileCreateSheet
            open={createOpen}
            onOpenChange={setCreateOpen}
            isLocked={(mode) =>
              !!mode.requiresPlan && !planAtLeast(planStatus?.plan, mode.requiresPlan)
            }
            onSelect={(mode, locked) => {
              setCreateOpen(false);
              if (locked) {
                 navigate({ to: local("/priser") });
                return;
              }
              if (mode.needsAuth && !user) {
                setAuthOpen(true);
                return;
              }
               if (mode.id === "single") navigate({ to: local("/skapa") });
              else
                navigate({
                  to: "/mina-prylar",
                  search: { lage: mode.id === "photos" ? "foton" : "film" },
                });
            }}
          />
        </Suspense>
      )}

      {authOpen && (
        <Suspense fallback={null}>
          <AuthModal open onOpenChange={setAuthOpen} />
        </Suspense>
      )}
    </>
  );
}
